<template>
  <section class="sigs">
    <div class="sigs__head">
      <span class="sigs__title">Подписи (PNG)</span>
      <span class="sigs__meta">{{ items.length }} / {{ maxCount }}, до {{ maxKb }} КБ</span>
    </div>

    <p v-if="hint" class="sigs__hint">{{ hint }}</p>
    <p v-if="error" class="sigs__error">{{ error }}</p>
    <p v-else-if="loading && !items.length" class="sigs__hint">Загрузка подписей…</p>
    <p v-else-if="!items.length" class="sigs__hint">Подписей ещё нет — загрузите PNG.</p>

    <ul v-if="items.length" class="sigs__list">
      <li v-for="item in items" :key="item.id" class="sigs__card">
        <div class="sigs__preview">
          <img v-if="previews[item.id]" :src="previews[item.id]" alt="Подпись" />
          <span v-else class="sigs__placeholder">PNG</span>
        </div>
        <div class="sigs__info">
          <strong>{{ item.file_name }}</strong>
          <span>{{ formatSize(item.size_bytes) }}</span>
        </div>
        <div class="sigs__actions">
          <label class="mini-btn">
            Заменить
            <input
              class="hidden"
              type="file"
              accept="image/png,.png"
              :disabled="busy"
              @change="onReplace($event, item)"
            />
          </label>
          <button class="mini-btn danger" type="button" :disabled="busy" @click="remove(item)">
            Удалить
          </button>
        </div>
      </li>
    </ul>

    <label v-if="items.length < maxCount" class="upload">
      <span>{{ busy ? 'Загрузка…' : 'Загрузить PNG' }}</span>
      <input
        class="hidden"
        type="file"
        accept="image/png,.png"
        :disabled="busy"
        @change="onCreate"
      />
    </label>
  </section>
</template>

<script>
import { onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import ApiConnector from '@/backend/auth/auth.js'
import { useErrorHandler } from '@/hooks/errorHandler'
import {
  MAX_SIGNATURE_PNG_BYTES,
  MAX_SIGNATURES_PER_USER,
  validateSignatureFile,
} from '@/pages/users/user-routing/userSignatureLimits.mjs'

const api = new ApiConnector()

export default {
  name: 'UserSignatureManager',
  props: {
    userId: { type: String, required: true },
  },
  emits: ['changed'],
  setup(props, { emit }) {
    const items = ref([])
    const previews = reactive({})
    const loading = ref(false)
    const busy = ref(false)
    const error = ref('')
    const hint = `PNG, не больше ${MAX_SIGNATURE_PNG_BYTES / 1024} КБ. У пользователя может быть несколько подписей.`

    const revokeAll = () => {
      Object.values(previews).forEach((url) => {
        if (url) URL.revokeObjectURL(url)
      })
      Object.keys(previews).forEach((key) => {
        delete previews[key]
      })
    }

    const loadPreview = async (item) => {
      try {
        const response = await api.getUserSignatureFile(props.userId, item.id)
        const blob = new Blob([response.data], { type: 'image/png' })
        if (previews[item.id]) URL.revokeObjectURL(previews[item.id])
        previews[item.id] = URL.createObjectURL(blob)
      } catch {
        /* preview optional */
      }
    }

    const load = async () => {
      if (!props.userId) return
      loading.value = true
      error.value = ''
      try {
        const response = await api.listUserSignatures(props.userId)
        items.value = Array.isArray(response.data) ? response.data : []
        revokeAll()
        await Promise.all(items.value.map(loadPreview))
      } catch (err) {
        error.value = 'Не удалось загрузить подписи'
        useErrorHandler(err)
      } finally {
        loading.value = false
      }
    }

    const formatUploadError = (err, fallback) => {
      const detail = err?.response?.data?.detail
      if (typeof detail === 'string' && detail.trim()) return detail
      if (Array.isArray(detail) && detail.length) {
        return detail.map((item) => item?.msg || item).filter(Boolean).join('; ')
      }
      return fallback
    }

    const pickFile = (event) => {
      const input = event.target
      const file = input?.files?.[0]
      input.value = ''
      return file
    }

    const onCreate = async (event) => {
      const file = pickFile(event)
      const check = validateSignatureFile(file)
      if (!check.ok) {
        error.value = check.message
        return
      }
      busy.value = true
      error.value = ''
      try {
        await api.uploadUserSignature(props.userId, file)
        emit('changed')
        await load()
      } catch (err) {
        error.value = formatUploadError(err, 'Не удалось загрузить PNG')
        useErrorHandler(err)
      } finally {
        busy.value = false
      }
    }

    const onReplace = async (event, item) => {
      const file = pickFile(event)
      const check = validateSignatureFile(file)
      if (!check.ok) {
        error.value = check.message
        return
      }
      busy.value = true
      error.value = ''
      try {
        await api.replaceUserSignature(props.userId, item.id, file)
        emit('changed')
        await load()
      } catch (err) {
        error.value = formatUploadError(err, 'Не удалось заменить PNG')
        useErrorHandler(err)
      } finally {
        busy.value = false
      }
    }

    const remove = async (item) => {
      if (!window.confirm(`Удалить подпись «${item.file_name}»?`)) return
      busy.value = true
      error.value = ''
      try {
        await api.deleteUserSignature(props.userId, item.id)
        emit('changed')
        await load()
      } catch (err) {
        error.value = 'Не удалось удалить подпись'
        useErrorHandler(err)
      } finally {
        busy.value = false
      }
    }

    const formatSize = (bytes) => {
      const n = Number(bytes) || 0
      if (n < 1024) return `${n} Б`
      return `${Math.round(n / 1024)} КБ`
    }

    watch(() => props.userId, () => {
      load()
    })

    onMounted(load)
    onBeforeUnmount(revokeAll)

    return {
      items,
      previews,
      loading,
      busy,
      error,
      hint,
      maxCount: MAX_SIGNATURES_PER_USER,
      maxKb: MAX_SIGNATURE_PNG_BYTES / 1024,
      onCreate,
      onReplace,
      remove,
      formatSize,
    }
  },
}
</script>

<style scoped>
.sigs {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
}

.sigs__head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: baseline;
}

.sigs__title {
  font-size: 13px;
  font-weight: 700;
  color: #333;
}

.sigs__meta,
.sigs__hint {
  color: #667085;
  font-size: 12px;
}

.sigs__error {
  margin: 0;
  color: #c0392b;
  font-size: 12px;
}

.sigs__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sigs__card {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 8px;
  border: 1px solid #edf0f3;
  border-radius: 8px;
  background: #f7f8fa;
}

.sigs__preview {
  width: 72px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: repeating-conic-gradient(#e5e7eb 0% 25%, #fff 0% 50%) 50% / 12px 12px;
  border-radius: 4px;
}

.sigs__preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.sigs__placeholder {
  font-size: 11px;
  color: #9ca3af;
}

.sigs__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: 12px;
  color: #667085;
}

.sigs__info strong {
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sigs__actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.mini-btn {
  height: 26px;
  border-radius: 4px;
  padding: 0 8px;
  border: 1px solid #d7dce2;
  background: #fff;
  color: #333;
  cursor: pointer;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
}

.mini-btn.danger {
  color: #9f2f2f;
  border-color: #e6a2a2;
}

.hidden {
  display: none;
}

.upload {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 12px;
  border: 1px dashed #93c5fd;
  border-radius: 6px;
  background: #eff6ff;
  color: #1d4ed8;
  cursor: pointer;
  font-size: 13px;
  width: fit-content;
}
</style>
